import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import api from '../api/client'

/**
 * UI-store: weergavemodus van de applicatie.
 *
 * Drie toestanden:
 * - uitgelogd            → publieke site (bezoekersweergave)
 * - ingelogd             → beheeromgeving
 * - ingelogd + preview   → bezoekersweergave met "terug naar beheer"-balk
 */
export const useUiStore = defineStore('ui', () => {
  const previewMode = ref(false)

  /** Beheeradres van vóór de voorbeeldweergave; zie startPreview. */
  const previewReturnPath = ref<string | null>(null)

  /** Mobiele navigatie (drawer onder het hamburgermenu in de header). */
  const isMobileNavOpen = ref(false)

  const authStore = useAuthStore()

  /** True wanneer de bezoekersweergave getoond wordt (uitgelogd óf preview). */
  const visitorView = computed(() => !authStore.isAuthenticated || previewMode.value)

  /** True wanneer beheer-affordances (menu's, +, chips) getoond mogen worden. */
  const canEdit = computed(() => authStore.isAuthenticated && !previewMode.value)

  /**
   * In voorbeeldweergave vraagt de app de API expliciet om het bezoekers-
   * beeld: afgeschermde categorieën zitten dan óók voor de beheerder op
   * slot, zodat je je eigen instelling kunt controleren. De header kan
   * alleen toegang wegnemen, nooit geven (zie ContentAccess).
   */
  function setPreviewHeader(active: boolean) {
    if (active) api.defaults.headers.common['X-Pentrical-Preview'] = '1'
    else delete api.defaults.headers.common['X-Pentrical-Preview']
  }

  /**
   * Voorbeeldweergave starten. Het beheeradres gaat mee zodat "terug naar
   * beheer" je terugbrengt waar je was: de voorbeeldweergave springt naar
   * het publieke adres van de pagina, en zonder dit blijf je daarna op dat
   * bezoekersadres hangen terwijl je weer aan het beheren bent.
   */
  function startPreview(returnPath?: string) {
    previewMode.value = true
    previewReturnPath.value = returnPath ?? null
    setPreviewHeader(true)
  }

  /** Geeft het beheeradres terug om naartoe te navigeren, of null. */
  function endPreview(): string | null {
    previewMode.value = false
    setPreviewHeader(false)

    const terug = previewReturnPath.value
    previewReturnPath.value = null

    return terug
  }

  function toggleMobileNav() {
    isMobileNavOpen.value = !isMobileNavOpen.value
  }

  function closeMobileNav() {
    isMobileNavOpen.value = false
  }

  return {
    previewMode,
    visitorView,
    canEdit,
    startPreview,
    endPreview,
    isMobileNavOpen,
    toggleMobileNav,
    closeMobileNav,
  }
})
